import { useState } from "react";
import { useWaves } from "@/context/DiningWaveContext";
import { useAuth } from "@/context/diningAuth";
import { Layers, Plus, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/diningApi";
import { toast } from "sonner";

export default function WaveSelector() {
  const { waves, currentWave, currentWaveId, setCurrentWaveId, refresh } = useWaves();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", clone_from: "none" });

  const choose = async (wid) => {
    setCurrentWaveId(wid);
    if (isAdmin) {
      try { await api.post(`/waves/${wid}/set-current`); } catch {}
    }
    setTimeout(() => window.location.reload(), 150);
  };

  const createWave = async () => {
    if (!form.name) return toast.error("Nombre obligatorio");
    try {
      const payload = {
        name: form.name,
        clone_from: form.clone_from === "none" ? null : form.clone_from,
      };
      const { data } = await api.post("/waves", payload);
      toast.success("Wave creada");
      setNewOpen(false);
      setForm({ name: "", clone_from: "none" });
      await refresh();
      choose(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="wave-selector-trigger"
            className="flex items-center gap-2 px-3 py-2 border border-zinc-200 bg-white hover:bg-zinc-50 text-sm transition-colors"
          >
            <Layers className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
            <div className="text-left">
              <p className="text-xs uppercase tracking-wider text-zinc-500">Wave</p>
              <p className="font-medium text-zinc-900">{currentWave?.name || "—"}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="rounded-none w-64" align="end">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider">Cambiar wave</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {waves.map((w) => (
            <DropdownMenuItem
              key={w.id}
              onClick={() => choose(w.id)}
              data-testid={`wave-item-${w.code}`}
              className="cursor-pointer flex items-center justify-between"
            >
              <div>
                <p className="font-medium">{w.name}</p>
                <p className="text-xs text-zinc-500 font-mono">{w.code}</p>
              </div>
              {w.id === currentWaveId && <Check className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />}
            </DropdownMenuItem>
          ))}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => { e.preventDefault(); setNewOpen(true); }}
                data-testid="wave-new"
                className="cursor-pointer text-zinc-900"
              >
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nueva wave
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Nueva wave</DialogTitle>
            <DialogDescription>Crea un periodo nuevo (ej: Wave 3 2026). Puedes clonar los restaurantes de una wave existente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="Wave 3 2026" data-testid="wave-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Clonar restaurantes desde</Label>
              <Select value={form.clone_from} onValueChange={(v) => setForm({ ...form, clone_from: v })}>
                <SelectTrigger className="rounded-none" data-testid="wave-clone-from"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">Empezar vacío</SelectItem>
                  {waves.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">Solo clona los restaurantes (nombre, ciudad, aforo, precio, IVA). Los eventos los subes con el Excel después.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createWave} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="wave-create-confirm">Crear y abrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
